/* © 2022 Promethean. All Rights Reserved.
 *
 * Unauthorized copying of this file or any part of this file
 * via any medium is strictly prohibited.
 */
import mixpanel, { Mixpanel, Persistence, XhrHeadersDef, Dict } from 'mixpanel-browser'

/**
 * User status values used by this library
 * 'signed in' | 'signed out'
 */
export enum UserStatus {
  SIGNED_IN = 'signed in',
  SIGNED_OUT = 'signed out'
}

/**
 * Some common super property keys used by promethean
 *
 * Adding values here can help with code completion and hints when
 * interacting with the super properties methods.
 *
 * Property names should use capitalized words and spaces by Promethean
 * convention, and not contain underscores, hyphens, or camel cased etc.
 */
export enum SuperProperty {
  APP_NAME = 'App Name',
  ORGANIZATION = 'Organization',
  USER_STATUS = 'User Status',
  JOB_TITLE = 'Job Title',
  ROLES = 'Roles'
}

/**
 * The default configuration values for our mixpanel instances
 */
export const DEFAULT_CONFIG: MixpanelConfig = {
  persistence: 'cookie'
}

/**
 * Key/Value property dictionary of values to send to mixpanel to track
 */
export interface MixpanelProperties extends Dict {
  [key: string]: any
}

/**
 * Key/Value property dictionary of values to send to mixpanel to track
 */
export type MixpanelSuperProperties = Dict & {
  [key in SuperProperty]?: string | undefined
}

/**
 * Update the configuration of a mixpanel library instance.
 *
 * The default config is:
 *
 * ```
 * {
 *   // HTTP method for tracking requests
 *   api_method: 'POST',
 *
 *   // transport for sending requests ('XHR' or 'sendBeacon')
 *   // NB: sendBeacon should only be used for scenarios such as
 *   // page unload where a "best-effort" attempt to send is
 *   // acceptable; the sendBeacon API does not support callbacks
 *   // or any way to know the result of the request. Mixpanel
 *   // tracking via sendBeacon will not support any event-
 *   // batching or retry mechanisms.
 *   api_transport: 'XHR',
 *
 *   // turn on request-batching/queueing/retry
 *   batch_requests: false,
 *
 *   // maximum number of events/updates to send in a single
 *   // network request
 *   batch_size: 50,
 *
 *   // milliseconds to wait between sending batch requests
 *   batch_flush_interval_ms: 5000,
 *
 *   // milliseconds to wait for network responses to batch requests
 *   // before they are considered timed-out and retried
 *   batch_request_timeout_ms: 90000,
 *
 *   // override value for cookie domain, only useful for ensuring
 *   // correct cross-subdomain cookies on unusual domains like
 *   // subdomain.mainsite.avocat.fr; NB this cannot be used to
 *   // set cookies on a different domain than the current origin
 *   cookie_domain: '',
 *
 *   // super properties cookie expiration (in days)
 *   cookie_expiration: 365,
 *
 *   // if true, cookie will be set with SameSite=None; Secure
 *   // this is only useful in special situations, like embedded
 *   // 3rd-party iframes that set up a Mixpanel instance
 *   cross_site_cookie: false,
 *
 *   // super properties span subdomains
 *   cross_subdomain_cookie: true,
 *
 *   // debug mode
 *   debug: false,
 *
 *   // if this is true, the mixpanel cookie or localStorage entry
 *   // will be deleted, and no user persistence will take place
 *   disable_persistence: false,
 *
 *   // if this is true, Mixpanel will automatically determine
 *   // City, Region and Country data using the IP address of the client
 *   ip: true,
 *
 *   // opt users out of tracking by this Mixpanel instance by default
 *   opt_out_tracking_by_default: false,
 *
 *   // opt users out of browser data storage by this Mixpanel instance by default
 *   opt_out_persistence_by_default: false,
 *
 *   // persistence mechanism used by opt-in/opt-out methods - cookie
 *   // or localStorage - falls back to cookie if localStorage is unavailable
 *   opt_out_tracking_persistence_type: 'localStorage',
 *
 *   // customize the name of cookie/localStorage set by opt-in/opt-out methods
 *   opt_out_tracking_cookie_prefix: null,
 *
 *   // type of persistent store for super properties (cookie/
 *   // localStorage) if set to 'localStorage', any existing
 *   // mixpanel cookie value with the same persistence_name
 *   // will be transferred to localStorage and deleted
 *   persistence: 'cookie',
 *
 *   // name for super properties persistent store
 *   persistence_name: '',
 *
 *   // names of properties/super-properties which should never
 *   // be sent with track() calls
 *   property_blacklist: [],
 *
 *   // if this is true, mixpanel cookies will be marked as
 *   // secure, meaning they will only be transmitted over https
 *   secure_cookie: false,
 *
 *   // the amount of time track_links will
 *   // wait for Mixpanel's servers to respond
 *   track_links_timeout: 300,
 *
 *   // if you set upgrade to be true, the library will check for
 *   // a cookie from our old js library and import super
 *   // properties from it, then the old cookie is deleted
 *   // The upgrade config option only works in the initialization,
 *   // so make sure you set it when you create the library.
 *   upgrade: false,
 *
 *   // extra HTTP request headers to set for each API request, in
 *   // the format {'Header-Name': value}
 *   xhr_headers: {},
 *
 *   // protocol for fetching in-app message resources, e.g.
 *   // 'https://' or 'http://'; defaults to '//' (which defers to the
 *   // current page's protocol)
 *   inapp_protocol: '//',
 *
 *   // whether to open in-app message link in new tab/window
 *   inapp_link_new_window: false,
 *
 *   // whether to ignore or respect the web browser's Do Not Track setting
 *   ignore_dnt: false,
 * }
 * ```
 */
export interface MixpanelConfig {
  api_host?: string
  api_method?: string
  api_transport?: string
  app_host?: string
  autotrack?: boolean
  cdn?: string
  cookie_domain?: string
  cross_site_cookie?: boolean
  cross_subdomain_cookie?: boolean
  persistence?: Persistence
  persistence_name?: string
  cookie_name?: string
  loaded?: (mixpanel: Mixpanel) => void
  store_google?: boolean
  save_referrer?: boolean
  test?: boolean
  verbose?: boolean
  img?: boolean
  debug?: boolean
  track_links_timeout?: number
  cookie_expiration?: number
  upgrade?: boolean
  disable_persistence?: boolean
  disable_cookie?: boolean
  disable_notifications?: boolean
  secure_cookie?: boolean
  ip?: boolean
  property_blacklist?: string[]
  xhr_headers?: XhrHeadersDef
  opt_out_tracking_by_default?: boolean
  opt_out_persistence_by_default?: boolean
  opt_out_tracking_persistence_type?: Persistence
  opt_out_tracking_cookie_prefix?: string
  inapp_protocol?: string
  inapp_link_new_window?: boolean
  ignore_dnt?: boolean
  batch_requests?: boolean
  batch_size?: number
  batch_flush_interval_ms?: number
  batch_request_timeout_ms?: number
}

/**
 * telemetryAppName is stored globally in memory to persist between super-property sessions
 */
let _telemetryAppName: string = 'undefined'

/**
 * Initialize a new global instance of the Telemetry Library.
 *
 * @param {object} options - initialization options
 * @param {string} options.token - environment specific mixpanel token
 * @param {string} [options.appName] - name of app to identify these events in the shared mixpanel
 * @param {string} [options.debug] - if true, mixpanel will log events to the console as they occur
 */
export function initialize(options: { token: string; appName?: string; debug?: boolean }) {
  mixpanel.init(options.token, {
    ...DEFAULT_CONFIG,
    debug: options.debug
  })

  // appName is intentionally stored in class as well as a super-property
  _telemetryAppName = options.appName || 'undefined'

  setSuperProperties({
    [SuperProperty.APP_NAME]: _telemetryAppName,
    [SuperProperty.USER_STATUS]: UserStatus.SIGNED_OUT
  })
}

/**
 * Track an event. This is the most important and frequently used telemetry function.
 *
 * Note: Event names should use capitalized words and spaces by Promethean
 * convention, and not contain underscores, hyphens, or camel cased etc.
 *
 * @param {string} eventName - The name of the event. This can be anything the user does - 'Click', 'Sign Up', etc.
 * @param {MixpanelProperties} [properties] - A set of properties to include with the event you're sending.
 */
export function track(eventName: string, properties?: Partial<MixpanelProperties>) {
  try {
    mixpanel.track(eventName, properties)
  } catch (e) {
    console.warn('Telemetry.track failed. Ensure initialize() has been called.', e)
  }
}

/**
 * Register a set of super properties, which are included with all events.
 * This will overwrite previous super property values.
 *
 * @param {MixpanelSuperProperties} properties - A set of properties to include with all sent events.
 */
export function setSuperProperties(properties: Partial<MixpanelSuperProperties>) {
  try {
    mixpanel.register(properties)
  } catch (e) {
    console.warn('Telemetry.setSuperProperties failed. Ensure initialize() has been called.', e)
  }
}

/**
 * Add a super property value stored with the current user session.
 *
 * @param {SuperProperty|string} property - super property name to add
 * @param {any} value - super property value to add
 */
export function addSuperProperty(property: SuperProperty | string, value: any) {
  try {
    mixpanel.register_once({ [property]: value })
  } catch (e) {
    console.warn('Telemetry.addSuperProperty failed. Ensure initialize() has been called.', e)
  }
}

/**
 * Delete a super property stored with the current user session.
 *
 * @param {SuperProperty|string} property - super property name to delete
 */
export function removeSuperProperty(property: SuperProperty | string) {
  try {
    mixpanel.unregister(property)
  } catch (e) {
    console.warn('Telemetry.removeSuperProperty failed. Ensure initialize() has been called.', e)
  }
}

/**
 * Identify a user with a unique userId and organization to track user activity across devices,
 * tie a user to their events, and create a user profile. If you never call this method, unique visitors
 * are tracked using a UUID generated the first time they visit the site.
 *
 * Call identify when you know the identity of the current user, typically after login or signup.
 * We recommend against using identify for anonymous visitors to your site.
 *
 * Notes:
 * If your project has ID Merge enabled, the identify method will connect pre- and post-authentication
 * events when appropriate.
 *
 * If your project does not have ID Merge enabled, identify will change the user's local distinct_id to
 * the unique ID you pass. Events tracked prior to authentication will not be connected to the same user
 * identity. If ID Merge is disabled, alias can be used to connect pre- and post-registration events.
 *
 * @param {object} properties - object containing user identification values userId and organization
 * @param {string} properties.userId - unique user id value
 * @param {string} properties.organization - user organization value
 */
export function identifyUser(properties: { userId: string; organization: string }) {
  try {
    // Set mixpanel unique identifier to userId
    mixpanel.identify(properties.userId)

    // Set common promethean user identity super properties
    mixpanel.register({
      [SuperProperty.ORGANIZATION]: properties.organization,
      [SuperProperty.USER_STATUS]: UserStatus.SIGNED_IN
    })
  } catch (e) {
    console.warn('Telemetry.identifyUser failed. Ensure initialize() has been called.', e)
  }
}

/**
 * Clears user and super properties and generates a new random distinct_id for this instance.
 * This is a good way to ensure session data is reset after a user logs out.
 *
 * The App Name and User Status are persisted after clearUser.
 */
export function clearUser() {
  try {
    // Reset clears everything
    mixpanel.reset()

    // We want to keep these super-props after clearing user
    setSuperProperties({
      [SuperProperty.APP_NAME]: _telemetryAppName,
      [SuperProperty.USER_STATUS]: UserStatus.SIGNED_OUT
    })
  } catch (e) {
    console.warn('Telemetry.clearUser failed. Ensure initialize() has been called.', e)
  }
}

/**
 * Change the app name later in the mixpanel lifecycle
 *
 * This can be useful if the same app is being used to host submodules that have different names.
 * @param appName
 */
export function updateAppName(appName: string) {
  _telemetryAppName = appName

  setSuperProperties({
    [SuperProperty.APP_NAME]: _telemetryAppName
  })
}

/**
 * Change the user Organization super property
 *
 * @param organization
 */
export function updateUserOrganization(organization: string) {
  setSuperProperties({
    [SuperProperty.ORGANIZATION]: organization
  })
}

/**
 * Opt the user out of data tracking and remove cookies/localstorage items
 */
export function optOutTracking() {
  try {
    mixpanel.opt_out_tracking()
  } catch (e) {
    console.warn('Telemetry.optOutTracking failed. Ensure initialize() has been called.', e)
  }
}

/**
 * Opt the user back in to data tracking. Only necessary if optOutTracking was called
 */
export function optInTracking() {
  try {
    mixpanel.opt_in_tracking()
  } catch (e) {
    console.warn('Telemetry.optInTracking failed. Ensure initialize() has been called.', e)
  }
}

/**
 * Returns true if this user has opted out of telemetry tracking previously
 */
export function hasOptedOutTracking(): boolean {
  try {
    return mixpanel.has_opted_out_tracking()
  } catch (e) {
    console.warn('Telemetry.hasOptedOutTracking failed. Ensure initialize() has been called.', e)
    return false
  }
}
